import React from 'react';
import { ArrowRight, ArrowUpRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROB_LINKEDIN_URL } from '../../components/course/courseContent';

type ChallengePageClarityHomeProps = {
  challengeName: string;
  description: string;
  steps: ReadonlyArray<{ title: string; body: string }>;
  questions: ReadonlyArray<{ q: string; a: string }>;
  registration: React.ReactNode;
  kickoffLabel: string | null;
  isLoading: boolean;
  registrationOpen: boolean;
  focusRegistrationForm: () => void;
};

/** Presentation only. The canonical challenge page owns content, dates, and registration. */
export default function ChallengePageClarityHome({
  challengeName,
  description,
  steps,
  questions,
  registration,
  kickoffLabel,
  isLoading,
  registrationOpen,
  focusRegistrationForm,
}: ChallengePageClarityHomeProps): React.ReactElement {
  return (
    <div className="challenge-home" data-testid="challenge-home">
      <header className="challenge-home__hero">
        <div className="challenge-home__intro">
          <h1>{challengeName}</h1>
          <p className="challenge-home__lead">{description}</p>
          <dl className="challenge-home__dates" data-testid="challenge-when">
            <div>
              <dt>Live kickoff</dt>
              <dd>{kickoffLabel || (isLoading ? 'Loading date…' : 'Date unavailable')}</dd>
            </div>
          </dl>
        </div>

        <aside className="challenge-home__enrollment" aria-label="Challenge registration">
          {registration}
        </aside>

        <figure className="challenge-home__mentor" data-testid="challenge-instructor">
          <img src="/images/rob.jpg" alt="Rob Grzesik" width="640" height="640" />
          <figcaption>
            <a
              className="challenge-home__text-link"
              href={ROB_LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Rob Grzesik <ArrowUpRight size={16} aria-hidden="true" />
            </a>
            <p>
              Managed $3B+ in transactions at Greystone · early Voyager Digital engineer (company
              peaked near $1B) · 15+ years in financial technology
            </p>
          </figcaption>
        </figure>
      </header>

      <section
        className="challenge-home__week"
        id="challenge-week"
        aria-labelledby="challenge-week-heading"
        data-testid="challenge-board"
      >
        <div className="challenge-home__section-heading">
          <h2 id="challenge-week-heading">How the week works</h2>
          <p>
            Build one AI trading workflow that researches the market, makes risk visible, and
            prepares a trade for your review.
          </p>
        </div>
        <ol className="challenge-home__steps" data-testid="challenge-outcome">
          {steps.map(({ title, body }) => (
            <li key={title}>
              <strong>{title}</strong>
              <p>{body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section
        className="challenge-home__questions"
        id="challenge-questions"
        data-testid="challenge-faq"
        aria-labelledby="challenge-questions-heading"
      >
        <h2 id="challenge-questions-heading">Questions</h2>
        <div className="challenge-home__faq-list">
          {questions.map(({ q, a }) => (
            <details key={q}>
              <summary>
                {q}
                <ChevronDown size={20} aria-hidden="true" />
              </summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>

      <div className="challenge-home__closing" data-testid="challenge-closing">
        <a
          className="challenge-home__primary"
          href="#challenge-register"
          onClick={focusRegistrationForm}
        >
          {registrationOpen ? 'Save my free seat' : 'See registration status'}
          <ArrowRight size={18} aria-hidden="true" />
        </a>
        <p>
          Want the full five-week course after the live session?{' '}
          <Link className="challenge-home__course-link" to="/courses/ai-trading-bootcamp">
            See the AI Trading Bootcamp
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
